import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Horizontal step indicator — replaces legacy uiComponents/Stepper (MUI styled). */

function TickIcon() {
  return (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M20.4215 4.32812L9.41465 19.671L5.07861 15.335" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

const stepperBase = {
  display: "grid",
  gap: 8,
  fontSize: 14,
  alignItems: "start",
};

const stepperLayouts = {
  2: { ...stepperBase, gridTemplateColumns: "1fr 1fr" },
  3: { ...stepperBase, gridTemplateColumns: "1fr 1fr 1fr", marginBottom: 64 },
  4: { ...stepperBase, gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 8 },
  5: { ...stepperBase, gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 8 },
  6: { ...stepperBase, gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 8 },
  7: { ...stepperBase, gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr", gap: 6, marginBottom: 8 },
};

function getStepperStyle(labelCount) {
  if (labelCount === 3) return stepperLayouts[3];
  if (labelCount === 5) return stepperLayouts[5];
  if (labelCount === 6) return stepperLayouts[6];
  if (labelCount === 7) return stepperLayouts[7];
  if (labelCount === 4) return stepperLayouts[4];
  return stepperLayouts[2];
}

export function Step({
  isActive = false,
  isComplete = false,
  label,
  thinInactiveConnector = false,
  compactLabels = false,
  style,
  ...rest
}) {
  const activeColor = "var(--foreground)";
  const inactiveColor = "var(--muted-foreground)";
  const color = isActive ? activeColor : inactiveColor;
  const connectorHeight = thinInactiveConnector ? (isActive ? 3 : 1) : 3;

  return (
    <div
      style={{
        color,
        textAlign: "center",
        fontWeight: isActive || isComplete ? 600 : 400,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 24,
          marginBottom: 4,
        }}
      >
        {isComplete ? <TickIcon /> : null}
      </div>
      <div
        style={{
          paddingBottom: 4,
          fontSize: compactLabels ? 12 : 14,
          lineHeight: 1.25,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          minHeight: compactLabels ? 40 : undefined,
        }}
      >
        {label}
      </div>
      <hr
        style={{
          border: 0,
          height: connectorHeight,
          marginTop: "auto",
          marginBottom: 16,
          background: isActive ? activeColor : inactiveColor,
          display: "block",
        }}
      />
    </div>
  );
}

export function StepperGrid({ labelCount, children, style, ...rest }) {
  return (
    <div style={{ ...getStepperStyle(labelCount || React.Children.count(children)), ...style }} {...rest}>
      {children}
    </div>
  );
}

export function StepperWithThreeSteps({ children, style, ...rest }) {
  return (
    <div style={{ ...stepperLayouts[3], ...style }} {...rest}>
      {children}
    </div>
  );
}

export function DynamicStepper({ labels, activeStep }) {
  const thinInactiveConnector = labels.length >= 6;
  const compactLabels = labels.length >= 7;
  const [stepsData, setStepsData] = React.useState(null);

  React.useEffect(() => {
    const steps = labels.map((label, index) => ({ label, isCompleted: activeStep > index }));
    setStepsData({ activeStep, steps });
  }, [activeStep, labels]);

  if (!stepsData) return null;

  return (
    <StepperGrid labelCount={labels.length}>
      {stepsData.steps.map((step, index) => (
        <Step
          key={step.label}
          isActive={stepsData.activeStep === index}
          isComplete={step.isCompleted}
          label={step.label}
          thinInactiveConnector={thinInactiveConnector}
          compactLabels={compactLabels}
        />
      ))}
    </StepperGrid>
  );
}

export default DynamicStepper;
